import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { ApiErrorResponse } from "../models/ApiResponse";
import TimeReport, { UpdateTimeReportRequest } from "../models/TimeReport";
import { QueryOpts } from "./api-hooks";
import { apiStore } from "../stores/api.store";

export type GetTimeReportsParams = {
	startDate?: string;
	endDate?: string;
	consultant?: string;
	customer?: string;
	job?: string;
	expand?: string;
};

/**
 * List time reports. Status filtering is deliberately NOT applied at the
 * backend level — all reports matching the date/consultant/customer/job
 * filters are loaded and filtered client-side (see
 * `filterAndSortTimeReports` in timeReport.utils), since distinctions like
 * FUTURE_TIME_REPORT vs PENDING or "INVOICED = has invoiceBatch" cannot be
 * expressed in simple backend queries.
 *
 * Each report gets a derived `shiftDuration` (hours) used by the duration
 * sort column.
 */
export function useTimeReports(query: GetTimeReportsParams, opts: QueryOpts = {}) {
	return useQuery(
		["time-reports", query],
		() =>
			apiStore
				.get<{ timeReports: TimeReport[] }>({
					path: "/time-report",
					isAuthRequired: true,
					query: {
						...query,
					},
				})
				.then((d) => ({
					timeReports: d.data.timeReports.map((timeReport) => ({
						...timeReport,
						shiftDuration: timeReport.shift
							? moment(timeReport.shift.end).diff(moment(timeReport.shift.start), "hours")
							: undefined,
					})),
				})),
		{
			...opts,
		}
	);
}

/** A single time report with the expands the details sidebar needs. */
export function useTimeReport(timeReportId?: string) {
	const { data, ...rest } = useQuery(
		["time-reports", "detail", timeReportId],
		() =>
			apiStore
				.get<TimeReport>({
					path: `/time-report/${timeReportId}`,
					query: {
						expand: "consultant,job,customer,shifts,metadata.user",
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled: !!timeReportId }
	);

	return {
		timeReport: data,
		...rest,
	};
}

/**
 * Update a time report. Toast-silent (callers own the feedback). Invalidates
 * the whole time-reports namespace so both the list and the details sidebar
 * refetch — the PUT response does not support expands, so a refetch is needed
 * anyway (this used to be TimeReportStore's update-then-reload).
 */
export function useUpdateTimeReport() {
	const queryClient = useQueryClient();

	return useMutation<
		unknown,
		ApiErrorResponse,
		{ timeReportId: string; update: UpdateTimeReportRequest }
	>(
		(params) =>
			apiStore
				.put({
					path: `/time-report/${params.timeReportId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["time-reports"]);
			},
		}
	);
}

export interface BulkUpdateTimeReportsRequest {
	timeReportIds: string[];
	data: { status: TimeReport["status"]; declineComment?: string };
}

export interface BulkUpdateTimeReportResult {
	id: string;
	ok: boolean;
	status?: TimeReport["status"];
	consultant?: { id: string; firstName?: string; lastName?: string };
	shift?: { id: string; start?: string; end?: string };
	customer?: { id: string; name?: string };
	error?: { code: string; detail?: string };
}

export interface BulkUpdateTimeReportsResponse {
	updatedCount: number;
	failedCount: number;
	results: BulkUpdateTimeReportResult[];
}

/** Max ids the bulk-update endpoint accepts per call (job-service MAX_BULK_UPDATE_IDS). */
export const BULK_UPDATE_TIME_REPORTS_CHUNK_SIZE = 1000;

/**
 * Set the same status on many time reports. Larger selections are sent in
 * sequential chunks of BULK_UPDATE_TIME_REPORTS_CHUNK_SIZE and merged into one
 * response. Reports that cannot be updated come back as failed rows; the rest
 * are still updated. Toast-silent.
 */
export function useBulkUpdateTimeReports() {
	const queryClient = useQueryClient();

	return useMutation<BulkUpdateTimeReportsResponse, ApiErrorResponse, BulkUpdateTimeReportsRequest>(
		async ({ timeReportIds, data }) => {
			const merged: BulkUpdateTimeReportsResponse = {
				updatedCount: 0,
				failedCount: 0,
				results: [],
			};

			// Sequential on purpose: each chunk already takes seconds server-side.
			for (let i = 0; i < timeReportIds.length; i += BULK_UPDATE_TIME_REPORTS_CHUNK_SIZE) {
				const { data: response } = await apiStore.post({
					path: `/time-report/bulk-update`,
					body: {
						timeReportIds: timeReportIds.slice(i, i + BULK_UPDATE_TIME_REPORTS_CHUNK_SIZE),
						data,
					},
					isAuthRequired: true,
				});
				const chunk = response as BulkUpdateTimeReportsResponse;

				merged.updatedCount += chunk.updatedCount;
				merged.failedCount += chunk.failedCount;
				merged.results.push(...chunk.results);
			}

			return merged;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["time-reports"]);
			},
		}
	);
}

/** Publish time reports to the customer. Toast-silent. */
export function usePublishTimeReports() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, string[]>(
		(timeReportIds) =>
			apiStore
				.post({
					path: `/time-report/publish`,
					body: { timeReportIds },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["time-reports"]);
			},
		}
	);
}
